export const DEFAULT_WEEKLY_CLASS_DAYS = 5;

export interface HeatmapThresholds {
  low: number;
  mid: number;
  max: number;
}

export function getHeatmapThresholds(weeklyClassDays?: number | null): HeatmapThresholds {
  const max = Math.max(weeklyClassDays || DEFAULT_WEEKLY_CLASS_DAYS, 1);
  return {
    low: Math.ceil(max * 0.25),
    mid: Math.ceil(max * 0.6),
    max,
  };
}

export type HeatmapBucketKey = 'empty' | 'low' | 'mid' | 'full';

export interface HeatmapLegendBucket {
  key: HeatmapBucketKey;
  label: string;
}

// Single source of truth for swatch styling. Both the heatmap cells and the
// popover legend pull bg/text classes from here so they cannot drift.
export const HEATMAP_BUCKET_STYLES: Record<HeatmapBucketKey, { bg: string; text: string }> = {
  empty: { bg: 'bg-slate-700/20', text: 'text-white/80' },
  low: { bg: 'bg-yellow-400/30', text: 'text-white/80' },
  mid: { bg: 'bg-yellow-400/60', text: 'text-slate-900' },
  full: { bg: 'bg-yellow-400', text: 'text-slate-900' },
};

export function getHeatmapBucketKey(count: number, thresholds: HeatmapThresholds): HeatmapBucketKey {
  if (count === 0) return 'empty';
  if (count <= thresholds.low) return 'low';
  if (count < thresholds.max) return 'mid';
  return 'full';
}

// Builds the legend buckets, collapsing the mid bucket when the range
// {low+1 .. max-1} would be empty or inverted (e.g., weekly_class_days <= 2).
export function getHeatmapLegendBuckets(thresholds: HeatmapThresholds): HeatmapLegendBucket[] {
  const { low, max } = thresholds;
  const buckets: HeatmapLegendBucket[] = [
    { key: 'empty', label: '0' },
    { key: 'low', label: low === 1 ? '1' : `1-${low}` },
  ];

  const midStart = low + 1;
  const midEnd = max - 1;
  if (midEnd >= midStart) {
    buckets.push({
      key: 'mid',
      label: midStart === midEnd ? `${midStart}` : `${midStart}-${midEnd}`,
    });
  }

  buckets.push({ key: 'full', label: `${max}` });
  return buckets;
}
